import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { translator } from "../../utils/Translator";
import {
  fetchLicenseStatus,
  hasSeenLicenseNotice,
  licenseNoticeKey,
  markLicenseNoticeSeen,
  requestLicenseRenew,
  shouldShowLicenseNotice,
} from "../../utils/LicenseExpire";
import LicenseChip from "./LicenseChip";
import LicenseDialog from "./LicenseDialog";

//หน่วงให้หน้าแรกหลัง login โหลดเสร็จก่อนค่อยเด้ง popup
export const AUTO_OPEN_DELAY = 1200;

/**
 * แจ้งเตือน License ใกล้หมดอายุ: chip บอกจำนวนวันบนแถบด้านบน + popup แจ้งขอต่ออายุ
 *
 * - render เป็น <li> ของ .header-notifications ใน Header — ไม่อยู่ในช่วงแจ้งเตือนจะไม่ render อะไรเลย
 *   (ไม่มีแถบเต็มความกว้างดันเนื้อหา หน้าที่สูงแบบ calc(100vh - …) จึงไม่ขยับ)
 * - โหลดสถานะครั้งเดียวต่อบริษัท — Header อยู่ตลอดการใช้งาน เปลี่ยนหน้าไม่โหลดซ้ำ
 * - popup เด้งเองครั้งเดียวต่อช่วง (30/15/7/3/1/หมดอายุ) ต่อผู้ใช้ และไม่เด้งเองถ้าบริษัทส่งคำขอแล้ว
 * - ปิดได้ทุกเมื่อ ไม่บล็อกการใช้งาน กด chip เพื่อเปิดอีกครั้ง
 */
const LicenseNotice = () => {
  const authUser = useSelector(({ auth }) => auth.authUser);
  const translate = useSelector(({ i18n }) => i18n.translate);
  const company = authUser ? authUser.company : null;
  const username = authUser ? authUser.username : null;
  //superuser ไม่มีบริษัทของตัวเอง จึงไม่มี License
  const isSuperUser = company === "#";

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  //กัน setState หลัง logout ระหว่างรอผลส่งคำขอ
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const t = useCallback(
    (key, fallback) => translator(translate ? translate[key] : null, fallback),
    [translate]
  );

  useEffect(() => {
    if (!company || isSuperUser) return undefined;

    let active = true;
    fetchLicenseStatus().then((result) => {
      if (active) setStatus(result);
    });
    return () => { active = false; };
  }, [company, isSuperUser]);

  useEffect(() => {
    if (!shouldShowLicenseNotice(status) || status.renewRequest) return undefined;

    const key = licenseNoticeKey(company, username, status);
    if (hasSeenLicenseNotice(key)) return undefined;

    const timer = setTimeout(() => {
      markLicenseNoticeSeen(key);
      setOpen(true);
    }, AUTO_OPEN_DELAY);
    return () => clearTimeout(timer);
  }, [status, company, username]);

  const handleOpen = useCallback(() => {
    setError("");
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleRequest = useCallback(async () => {
    setSending(true);
    setError("");

    try {
      const result = await requestLicenseRenew();
      if (mountedRef.current && result && result.status) setStatus(result.status);
    } catch (e) {
      //isb-api ตอบข้อความภาษาไทยที่บอกสาเหตุ (400 ตั้งค่าไม่ครบ / 503 ส่งอีเมลไม่สำเร็จ) — แสดงตามนั้น
      const message = e && e.response && e.response.data ? e.response.data.message : "";
      if (mountedRef.current) {
        setError(typeof message === "string" && message
          ? message
          : t("ISA_License_RequestFailed", "ส่งคำขอไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
      }
    }

    if (mountedRef.current) setSending(false);
  }, [t]);

  if (!shouldShowLicenseNotice(status)) return null;

  return (
    <li className="list-inline-item">
      <LicenseChip status={status} t={t} onClick={handleOpen} />
      <LicenseDialog
        open={open}
        status={status}
        t={t}
        sending={sending}
        error={error}
        onClose={handleClose}
        onRequest={handleRequest}
      />
    </li>
  );
};

export default memo(LicenseNotice);
